import { getErrMsg } from '../lib/errorMsg';
import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { AlertTriangle, Bell, CheckCheck, Trash2, RefreshCw, ScanLine } from 'lucide-react';

const SEVERITY_STYLES = {
  critical: { badge: 'bg-red-50 text-red-700 border-red-200', dot: 'bg-red-500', icon: AlertTriangle, iconColor: 'text-red-500' },
  warning: { badge: 'bg-amber-50 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle, iconColor: 'text-amber-500' },
  info: { badge: 'bg-blue-50 text-blue-700 border-blue-200', dot: 'bg-blue-500', icon: Bell, iconColor: 'text-blue-500' },
};

const TYPE_LABELS = {
  margin_below_threshold: 'Margin Below Threshold',
  rm_price_change: 'RM Price Change',
  rm_price_exceeded_locked: 'Market RM Exceeds Locked Cost',
};

function AlertCard({ alert, onRead, onDelete }) {
  const style = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.info;
  const Icon = style.icon;

  return (
    <div data-testid={`alert-card-${alert.alert_id}`}
      className={`bg-white border border-slate-200 rounded-sm shadow-sm p-4 flex gap-4 transition-all duration-200 ${alert.is_read ? 'opacity-70' : 'border-l-4'} ${!alert.is_read ? `border-l-${alert.severity === 'critical' ? 'red' : alert.severity === 'warning' ? 'amber' : 'blue'}-500` : ''}`}
      style={!alert.is_read ? { borderLeftColor: alert.severity === 'critical' ? '#ef4444' : alert.severity === 'warning' ? '#f59e0b' : '#3b82f6' } : {}}
    >
      <div className={`w-9 h-9 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5 ${alert.severity === 'critical' ? 'bg-red-50' : alert.severity === 'warning' ? 'bg-amber-50' : 'bg-blue-50'}`}>
        <Icon size={16} className={style.iconColor} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${style.badge}`}>
              {alert.severity?.toUpperCase()}
            </span>
            <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-sm">
              {TYPE_LABELS[alert.type] || alert.type}
            </span>
            {!alert.is_read && (
              <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0"></span>
            )}
          </div>
          <p className="text-xs text-slate-400 flex-shrink-0 font-mono">
            {alert.created_at ? new Date(alert.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}
          </p>
        </div>
        <p className="text-sm text-slate-800 mt-1.5 leading-relaxed">{alert.message}</p>
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          <span className="font-mono text-xs text-slate-500">Part: <strong>{alert.part_number}</strong></span>
          <span className="text-xs text-slate-500">Customer: <strong>{alert.customer_name}</strong></span>
          {alert.old_margin !== undefined && alert.old_margin !== 0 && alert.type !== 'rm_price_exceeded_locked' && (
            <span className="font-mono text-xs text-slate-500">
              Margin: <span className="line-through text-red-400">{Number(alert.old_margin).toFixed(1)}%</span>
              {' → '}
              <span className={alert.new_margin < 15 ? 'text-red-600 font-bold' : 'text-amber-600'}>{Number(alert.new_margin).toFixed(1)}%</span>
            </span>
          )}
          {alert.old_price !== undefined && (
            <span className="font-mono text-xs text-slate-500">
              Price: ₹{alert.old_price} → ₹{alert.new_price}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 mt-3">
          {!alert.is_read && (
            <button onClick={() => onRead(alert.alert_id)}
              data-testid={`mark-read-${alert.alert_id}`}
              className="text-xs font-semibold text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors">
              <CheckCheck size={12} /> Mark as Read
            </button>
          )}
          <button onClick={() => onDelete(alert.alert_id)}
            data-testid={`delete-alert-${alert.alert_id}`}
            className="text-xs font-semibold text-red-400 hover:text-red-600 flex items-center gap-1 transition-colors ml-auto">
            <Trash2 size={12} /> Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, critical, warning, info
  const [scanning, setScanning] = useState(false);

  const handleScanRmVariance = async () => {
    setScanning(true);
    try {
      const res = await api.post('/alerts/scan-rm-variance');
      toast.success(`Scan complete — ${res.data.alerts_created} new RM variance alerts from ${res.data.scanned} parts`);
      fetchAlerts();
    } catch (err) { toast.error(getErrMsg(err)); }
    finally { setScanning(false); }
  };

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const res = await api.get('/alerts');
      setAlerts(res.data);
    } catch {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAlerts(); }, []);

  const handleRead = async (id) => {
    try {
      await api.put(`/alerts/${id}/read`);
      setAlerts(prev => prev.map(a => a.alert_id === id ? { ...a, is_read: true } : a));
    } catch {
      toast.error('Failed to mark as read');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      setAlerts(prev => prev.filter(a => a.alert_id !== id));
      toast.success('Alert dismissed');
    } catch {
      toast.error('Failed to dismiss alert');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/alerts/read-all');
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
      toast.success('All alerts marked as read');
    } catch {
      toast.error('Failed to update alerts');
    }
  };

  const filtered = alerts.filter(a => {
    if (filter === 'unread') return !a.is_read;
    if (filter === 'critical') return a.severity === 'critical';
    if (filter === 'warning') return a.severity === 'warning';
    if (filter === 'info') return a.severity === 'info';
    return true;
  });

  const unreadCount = alerts.filter(a => !a.is_read).length;
  const criticalCount = alerts.filter(a => a.severity === 'critical').length;

  const filterTabs = [
    { key: 'all', label: 'All', count: alerts.length },
    { key: 'unread', label: 'Unread', count: unreadCount },
    { key: 'critical', label: 'Critical', count: criticalCount },
    { key: 'warning', label: 'Warning', count: alerts.filter(a => a.severity === 'warning').length },
    { key: 'info', label: 'Info', count: alerts.filter(a => a.severity === 'info').length },
  ];

  return (
    <div className="space-y-5 max-w-4xl" data-testid="alerts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading font-bold text-slate-900 text-xl">Costing Variance Alerts</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            {unreadCount > 0 ? (
              <span className="text-red-500 font-semibold">{unreadCount} unread alert{unreadCount !== 1 ? 's' : ''}</span>
            ) : (
              'All alerts reviewed'
            )}
            {' · '}{alerts.length} total
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchAlerts} data-testid="refresh-alerts"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-300 rounded-sm text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            <RefreshCw size={13} /> Refresh
          </button>
          <button onClick={handleScanRmVariance} disabled={scanning} data-testid="scan-rm-variance-btn"
            className="flex items-center gap-1.5 px-3 py-2 bg-white border border-amber-300 rounded-sm text-amber-700 text-sm font-semibold hover:bg-amber-50 transition-colors">
            {scanning ? <div className="w-3 h-3 border-2 border-amber-300 border-t-amber-600 rounded-full animate-spin" /> : <ScanLine size={13} />}
            Scan RM Variance
          </button>
          {unreadCount > 0 && (
            <button onClick={handleMarkAllRead} data-testid="mark-all-read-btn"
              className="flex items-center gap-1.5 px-3 py-2 bg-slate-900 text-white text-sm font-bold uppercase rounded-sm hover:bg-slate-800 transition-colors">
              <CheckCheck size={13} /> Mark All Read
            </button>
          )}
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 bg-white border border-slate-200 rounded-sm p-1 flex-wrap">
        {filterTabs.map(tab => (
          <button key={tab.key} onClick={() => setFilter(tab.key)}
            data-testid={`filter-${tab.key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-sm transition-colors ${
              filter === tab.key ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}>
            {tab.label}
            {tab.count > 0 && (
              <span className={`text-xs font-mono px-1.5 py-0.5 rounded-full ${filter === tab.key ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Alerts List */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3].map(i => <div key={i} className="h-28 bg-slate-200 rounded-sm animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-sm p-12 text-center shadow-sm">
          <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCheck size={24} className="text-emerald-600" />
          </div>
          <h3 className="font-heading font-bold text-slate-900 mb-1">
            {filter === 'all' ? 'No alerts yet' : `No ${filter} alerts`}
          </h3>
          <p className="text-slate-500 text-sm">
            {filter === 'all'
              ? 'Alerts are generated when RM prices change and affect part margins or costs. Use "Scan RM Variance" to check all parts against current market prices.'
              : `Switch to "All" to see other alerts`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(alert => (
            <AlertCard key={alert.alert_id} alert={alert} onRead={handleRead} onDelete={handleDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
